
import { useState } from "react";
import Link from "@/components/ui/link-compat";
import type { AppColumnDef } from "@/lib/table-features";
import type { SuperAdminOrganization } from "@/types/superadmin";
import { textFilterFn } from "@/lib/table-filters";
import { formatDate } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function DeleteOrgButton({ org, onDelete }: { org: SuperAdminOrganization; onDelete: (orgId: string) => void }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={() => setOpen(true)} className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10">
        <Trash2 className="h-3.5 w-3.5" />
        Delete
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogMedia className="bg-red-100 dark:bg-red-950">
              <Trash2 className="size-8 text-red-600 dark:text-red-400" />
            </AlertDialogMedia>
            <AlertDialogTitle>Delete organization?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <p>
                This will permanently delete <strong>{org.name}</strong> and all its data including contacts, deals, tasks, and notes. This cannot be undone.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { onDelete(org.id); setOpen(false); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function getOrganizationColumns(onDelete?: (orgId: string) => void): AppColumnDef<SuperAdminOrganization, unknown>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <Link href={`/dashboard/settings/admin/organization?id=${row.original.id}`} prefetch={false} className="font-medium text-blue-600 hover:underline">
          {row.original.name}
        </Link>
      ),
      filterFn: textFilterFn,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.description || "-"}
        </span>
      ),
      filterFn: textFilterFn,
    },
    {
      accessorKey: "memberCount",
      header: "Members",
      cell: ({ row }) => (
        <Badge variant="secondary">{row.original.memberCount}</Badge>
      ),
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm whitespace-nowrap">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      size: 100,
      cell: ({ row }) =>
        onDelete ? (
          <DeleteOrgButton org={row.original} onDelete={onDelete} />
        ) : null,
    },
  ];
}
